import { Button } from 'src/design-system'
import { flexRender } from '@tanstack/react-table'
import { MinusCircle, UsersThree } from '@phosphor-icons/react'
import { observer } from 'mobx-react-lite'
import * as React from 'react'
import { SearchFilter } from 'components/search-filter'
import { Select } from 'components/atoms/select'
import { store } from 'store/index'
import { trackEvent } from 'app/packs/src/services/event-tracker'
import { useTable } from './context/table'
import * as Table from 'components/table'
import { OverviewCards, SkillTypeFilter } from '../overview-cards'
import * as SecondaryNav from 'components/secondary-nav'
import { SkillDetailPanel } from '../skill-detail-panel'

export const OrgPersonalSkillsTable = observer(() => {
  const {
    columnFilters,
    data,
    globalFilter,
    setGlobalFilter,
    table,
    loading,
    selectedSkillId,
    setSelectedSkillId,
  } = useTable()

  const selectedSkill = React.useMemo(() => {
    if (!selectedSkillId) return null
    return data.find((d) => d.id === selectedSkillId) || null
  }, [selectedSkillId, data])

  const source = 'org-personal-skills-table'

  const [selectedSkillType, setSelectedSkillType] =
    React.useState<SkillTypeFilter>('all')

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const filterValue = (columnId: string) => {
    return columnFilters.find((column) => column.id === columnId)?.value
  }

  const teamOptions = React.useMemo(() => {
    return store.teams.forOrg.map((team) => ({
      label: team.name,
      value: team.id,
    }))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.teams.forOrg])

  const onClearFilters = () => {
    table.resetColumnFilters()
    table.resetGlobalFilter()
    table.resetSorting()
    setSelectedSkillType('all')
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onFilterChange = (column: string, value: string | string[]) => {
    table.getColumn(column)?.setFilterValue(value)
    trackEvent('$track_org_personal_skills_filter_changed', { [column]: value })
  }

  const onSkillTypeClick = (type: SkillTypeFilter) => {
    setSelectedSkillType(type)
    if (type === 'all') {
      table.getColumn('skillType')?.setFilterValue(undefined)
    } else {
      table.getColumn('skillType')?.setFilterValue(type)
    }
    trackEvent('$track_org_personal_skills_type_filter', { type })
  }

  const showClearFiltersButton = React.useMemo(() => {
    return columnFilters.length > 0 || (globalFilter && globalFilter !== '')
  }, [columnFilters, globalFilter])

  const counts = React.useMemo(() => {
    const all = data.length
    const hard = data.filter((d) => d.skillType === 'hard').length
    const soft = data.filter((d) => d.skillType === 'soft').length
    return { all, hard, soft }
  }, [data])

  return (
    <div>
      <div className="px-4 pt-4 pb-2">
        <SecondaryNav.Header>Personal skills</SecondaryNav.Header>
      </div>
      <OverviewCards
        counts={counts}
        selectedSkillType={selectedSkillType}
        onSkillTypeClick={onSkillTypeClick}
      />
      <div className="pt-4">
        <div className="flex flex-wrap gap-2 mb-5 px-4">
          <SearchFilter
            name="org-personal-skills-search"
            onChange={(value) => {
              setGlobalFilter(String(value))
            }}
            value={globalFilter ?? ''}
          />
          <Select
            isMulti
            isSearchable
            onChange={(values: string[]) => {
              // Team filtering would require backend support
              trackEvent('$track_org_personal_skills_team_filter', {
                teams: values,
              })
            }}
            options={teamOptions}
            triggerContent={<TeamTriggerContent />}
          />
          {showClearFiltersButton && (
            <Button
              className="h-[34px]"
              onClick={onClearFilters}
              variant="outline"
            >
              <MinusCircle
                aria-hidden
                className="h-4 text-theme-40 w-4"
                weight="bold"
              />
              Clear filters
            </Button>
          )}
        </div>
        <div className="overflow-x-auto">
          <Table.Root>
            <Table.Head>
              {table.getHeaderGroups().map((headerGroup) => (
                <Table.Row key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <Table.Header
                      key={header.id}
                      header={header}
                      loading={loading}
                      source={source}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </Table.Header>
                  ))}
                </Table.Row>
              ))}
            </Table.Head>

            <Table.Body>
              {table.getRowModel().rows.length === 0 && (
                <Table.NoResultsRow colspan={table.getAllColumns().length} />
              )}

              {table.getRowModel().rows.map((row, index) => {
                const cells = row.getVisibleCells()

                return (
                  <Table.Row
                    showBottomBorder={
                      index < table.getRowModel().rows.length - 1
                    }
                    key={row.id}
                  >
                    {cells.map((cell) => (
                      <Table.Cell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </Table.Cell>
                    ))}
                  </Table.Row>
                )
              })}
            </Table.Body>
          </Table.Root>
          <div className="px-3 flex justify-center p-[60px]">
            <Table.Pagination table={table} />
          </div>
        </div>
      </div>
      <SkillDetailPanel
        skill={selectedSkill}
        open={selectedSkillId !== null}
        onClose={() => setSelectedSkillId(null)}
      />
    </div>
  )
})

const TeamTriggerContent: React.VFC = () => {
  return (
    <div className="flex gap-1 items-center">
      <UsersThree aria-hidden className="h-4 text-gray-600 w-4" weight="bold" />
      All teams
    </div>
  )
}
